/**
 * TestCaseDetail — right pane of the three-pane layout.
 *
 * Handles both view/edit mode (existing test case) and create mode
 * (selectedTestCaseId === '__new__').
 *
 * Props:
 *   isFullscreen — when true, the pane fills all available width.
 *   onClose      — closes the pane (sets selectedId to null on desktop,
 *                  dismisses the mobile overlay). Shown as the X button
 *                  in the header.
 *
 * Tabs:
 *  - Details   — description, preconditions, caseType, folder
 *  - Steps     — step builder
 *  - Comments  — comment thread with image paste
 *  - History   — audit log
 */

import { useState, useEffect } from 'react';
import { Loader2, Save, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { useTestCasesStore } from '../../../store/useTestCasesStore';
import { API_BASE_URL } from '../../../config/api';
import { TestCaseDetailHeader }  from './TestCaseDetailHeader';
import { TestCaseDetailsTab }    from './TestCaseDetailsTab';
import { TestCaseStepsTab, buildEmptyStep, newLocalId } from './TestCaseStepsTab';
import { TestCaseCommentsTab }   from './TestCaseCommentsTab';
import { TestCaseHistoryTab }    from './TestCaseHistoryTab';
import type { IStepDraft } from './TestCaseStepsTab';
import type { ITestCaseRow, TestSeverity, TestType, TestCaseType } from '../types';
import { NEW_TEST_CASE_ID } from '../types';

const API_URL = API_BASE_URL;

export type TabId = 'details' | 'steps' | 'comments' | 'history';

const TABS: Array<{ id: TabId; label: string }> = [
    { id: 'details',  label: 'Details' },
    { id: 'steps',    label: 'Steps' },
    { id: 'comments', label: 'Comments' },
    { id: 'history',  label: 'History' },
];

interface TestCaseDetailProps {
    testCase:      ITestCaseRow | null;  // null when selectedTestCaseId === '__new__'
    projectId:     string | null;
    folderId?:     string | null;
    isFullscreen?: boolean;
    onExpand?:     () => void;
    onClose?:      () => void;
    /** Controlled active tab — when provided, tab state is owned by the parent. */
    activeTab?:    TabId;
    onTabChange?:  (tab: TabId) => void;
}

export function TestCaseDetail({
    testCase,
    projectId,
    folderId,
    isFullscreen = false,
    onExpand,
    onClose,
    activeTab:    controlledTab,
    onTabChange,
}: TestCaseDetailProps) {
    const { token }   = useAuth();
    const queryClient = useQueryClient();
    const setSelected = useTestCasesStore((s) => s.setSelectedTestCaseId);
    const selectedId  = useTestCasesStore((s) => s.selectedTestCaseId);

    const isNew = selectedId === NEW_TEST_CASE_ID;

    // ── Form state ────────────────────────────────────────────────────────────
    const [title,         setTitle]         = useState('');
    const [description,   setDescription]   = useState('');
    const [preconditions, setPreconditions] = useState('');
    const [suite,         setSuite]         = useState('');
    const [type,          setType]          = useState<TestType>('MANUAL');
    const [severity,      setSeverity]      = useState<TestSeverity>('MEDIUM');
    const [caseType,      setCaseType]      = useState<TestCaseType>('FUNCTIONAL');
    const [tags,          setTags]          = useState<string[]>([]);
    const [steps,         setSteps]         = useState<IStepDraft[]>([buildEmptyStep()]);

    // Tab state: controlled by parent when activeTab prop is provided, otherwise local.
    const [localTab,  setLocalTab]  = useState<TabId>('details');
    const activeTab = controlledTab ?? localTab;
    function setActiveTab(tab: TabId) { setLocalTab(tab); onTabChange?.(tab); }
    const [saving,     setSaving]     = useState(false);
    const [deleting,   setDeleting]   = useState(false);
    const [cloning,    setCloning]    = useState(false);
    const [saveError,  setSaveError]  = useState<string | null>(null);
    const [isDirty,    setIsDirty]    = useState(false);

    // ── Populate form when test case changes ─────────────────────────────────
    useEffect(() => {
        if (!testCase || isNew) {
            setTitle('');
            setDescription('');
            setPreconditions('');
            setSuite('');
            setType('MANUAL');
            setSeverity('MEDIUM');
            setCaseType('FUNCTIONAL');
            setTags([]);
            setSteps([buildEmptyStep()]);
            // Only reset tab when uncontrolled (parent manages it when controlled).
            if (!controlledTab) setLocalTab('details');
            setSaveError(null);
            setIsDirty(false);
            return;
        }
        setTitle(testCase.title);
        setDescription(testCase.description ?? '');
        setPreconditions(testCase.preconditions ?? '');
        setSuite(testCase.suite ?? '');
        setType(testCase.type);
        // Fallback defaults for legacy test cases that lack v2 fields
        setSeverity(testCase.severity ?? 'MEDIUM');
        setCaseType(testCase.caseType ?? 'FUNCTIONAL');
        setTags(testCase.tags ?? []);
        setSteps(
            testCase.steps && testCase.steps.length > 0
                ? testCase.steps.map((s) => ({ localId: newLocalId(), action: s.action, expectedResult: s.expectedResult }))
                : [buildEmptyStep()],
        );
        setSaveError(null);
        setIsDirty(false);
    }, [testCase?._id, isNew]);

    // Mark dirty on any form change (except initial population)
    useEffect(() => { setIsDirty(true); }, [title, description, preconditions, suite, type, severity, caseType, tags, steps]);

    function invalidate() {
        queryClient.invalidateQueries({ queryKey: ['test-cases'] });
    }

    // ── Close helper — prefers caller's onClose, falls back to store ──────────
    function handleClose() {
        if (onClose) onClose();
        else setSelected(null);
    }

    // ── Save ─────────────────────────────────────────────────────────────────
    async function handleSave() {
        if (!title.trim()) { setSaveError('Title is required.'); return; }
        setSaveError(null);
        setSaving(true);
        try {
            const nonEmptySteps = steps.filter((s) => s.action.trim() && s.expectedResult.trim());
            const payload = {
                title:        title.trim(),
                description:  description.trim() || undefined,
                suite:        suite.trim()        || undefined,
                preconditions: preconditions.trim() || undefined,
                type,
                severity,
                caseType,
                tags:         tags.length > 0 ? tags : undefined,
                steps:        nonEmptySteps.map((s) => ({ action: s.action.trim(), expectedResult: s.expectedResult.trim(), status: 'PENDING' })),
                folderId:     folderId ?? undefined,
            };

            if (isNew) {
                if (!projectId) { setSaveError('No project selected.'); return; }
                const { data } = await axios.post(
                    `${API_URL}/api/test-cases`,
                    { ...payload, projectId },
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                invalidate();
                setSelected(data.data._id as string);
            } else {
                await axios.put(
                    `${API_URL}/api/test-cases/${testCase!._id}`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                invalidate();
                setIsDirty(false);
            }
        } catch (err: unknown) {
            const msg = axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : 'Failed to save';
            setSaveError(msg);
        } finally {
            setSaving(false);
        }
    }

    // ── Delete ────────────────────────────────────────────────────────────────
    async function handleDelete() {
        if (!testCase) return;
        if (!window.confirm(`Delete "${testCase.title}"? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            await axios.delete(`${API_URL}/api/test-cases/${testCase._id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            invalidate();
            handleClose();
        } catch {
            // Show nothing — list will refresh on next query
        } finally {
            setDeleting(false);
        }
    }

    // ── Clone ─────────────────────────────────────────────────────────────────
    async function handleClone() {
        if (!testCase) return;
        setCloning(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/test-cases/clone`,
                { mode: 'cases', ids: [testCase._id], targetFolderId: testCase.folderId },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            invalidate();
            const cloned = data.data?.cases?.[0];
            if (cloned?._id) setSelected(cloned._id as string);
        } catch {
            // silent
        } finally {
            setCloning(false);
        }
    }

    // ── Width: 400px inline, full-width in fullscreen ────────────────────────
    const panelWidth = isFullscreen
        ? 'w-full'
        : 'w-full md:w-[400px] md:flex-shrink-0';

    return (
        <div data-testid="test-case-detail" className={`flex flex-col ${panelWidth} border-l border-slate-200 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark overflow-hidden h-full`}>
            {/* Header — contains close button top-right */}
            <TestCaseDetailHeader
                tcId={testCase?.tcId}
                title={title}
                setTitle={(v) => { setTitle(v); setIsDirty(true); }}
                severity={severity}
                setSeverity={(v) => { setSeverity(v); setIsDirty(true); }}
                type={type}
                setType={(v) => { setType(v); setIsDirty(true); }}
                isNew={isNew}
                isFullscreen={isFullscreen}
                onExpand={onExpand}
                onClose={handleClose}
                onDelete={handleDelete}
                onClone={handleClone}
                isDeleting={deleting}
                isCloning={cloning}
            />

            {/* Tabs */}
            <div role="tablist" className="flex border-b border-slate-200 dark:border-gh-border-dark flex-shrink-0 overflow-x-auto">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        data-testid={`detail-tab-${tab.id}`}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 min-w-[60px] py-2 text-xs font-medium transition-colors border-b-2 whitespace-nowrap
                            ${activeTab === tab.id
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                {activeTab === 'details' && (
                    <TestCaseDetailsTab
                        description={description}     setDescription={(v) => { setDescription(v);     setIsDirty(true); }}
                        preconditions={preconditions} setPreconditions={(v) => { setPreconditions(v); setIsDirty(true); }}
                        suite={suite}                 setSuite={(v) => { setSuite(v);                 setIsDirty(true); }}
                        caseType={caseType}           setCaseType={(v) => { setCaseType(v);           setIsDirty(true); }}
                        tags={tags}                   setTags={(v) => { setTags(v);                   setIsDirty(true); }}
                        projectId={projectId}
                        folderPath={testCase?.folderPath}
                        createdBy={testCase?.createdBy}
                        createdAt={testCase?.createdAt}
                    />
                )}

                {activeTab === 'steps' && (
                    <TestCaseStepsTab
                        steps={steps}
                        onChange={(s) => { setSteps(s); setIsDirty(true); }}
                    />
                )}

                {activeTab === 'comments' && (
                    <TestCaseCommentsTab testCaseId={isNew ? null : (testCase?._id ?? null)} />
                )}

                {activeTab === 'history' && (
                    <TestCaseHistoryTab testCaseId={isNew ? null : (testCase?._id ?? null)} />
                )}
            </div>

            {/* Footer — Save + error (no close button here; it's in the header) */}
            <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 flex-shrink-0">
                {saveError && (
                    <div className="flex items-center gap-1.5 text-xs text-rose-500 mb-2">
                        <AlertCircle size={12} /> {saveError}
                    </div>
                )}
                <button
                    data-testid="test-case-detail-save-button"
                    onClick={handleSave}
                    disabled={saving || (!isDirty && !isNew)}
                    className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-lg disabled:opacity-50 transition-colors"
                >
                    {saving
                        ? <><Loader2 size={12} className="animate-spin" /> Saving…</>
                        : <><Save size={12} /> {isNew ? 'Create Test Case' : 'Save Changes'}</>
                    }
                </button>
            </div>
        </div>
    );
}
